'use client';

import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { AplusFragmentFragmentDoc } from '@packages/gql/generated/shopping/AplusFragmentFragmentDoc';
import { ToggleServiceProductDocument } from '@packages/gql/generated/shopping/ToggleServiceProductDocument';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useAplusSelection } from '@packages/modules/src/APlus/hooks/useAplusSelection/useAplusSelection';
import { AplusAccordion } from '@packages/modules/src/APlus/AplusAccordion/AplusAccordion.client';
import { transformBasketAplusToItems } from '@packages/modules/src/APlus/helper/transformBasketAplusToItems';

/* GraphQL */ `
  fragment AplusFragment on BasketAPlus {
    clusters {
      label
      services {
        serviceType
        serviceIcon
        uuid
        name
        price {
          value
          currency
        }
        included
        layerLink
        selected
        serviceCount {
          current
          maximum
        }
      }
    }
  }
`;

/* GraphQL */ `
  mutation ToggleServiceProduct(
    $locale: String!
    $lineItemId: String!
    $serviceUuid: String!
    $count: Int
  ) {
    toggleServiceProduct(
      locale: $locale
      lineItemId: $lineItemId
      serviceUuid: $serviceUuid
      count: $count
    ) {
      ...BasketFragment
    }
  }
`;

type BasketAplusProps = {
  aplusData: FragmentType<typeof AplusFragmentFragmentDoc>;
  lineItemId: string;
  quantity: number;
};

export const BasketAplus = ({ aplusData, lineItemId, quantity }: BasketAplusProps) => {
  const { locale } = useIntl();
  const [, toggleServiceProduct] = useAuthMutation(ToggleServiceProductDocument);

  const fragmentData = getFragmentData(AplusFragmentFragmentDoc, aplusData);
  const { items, currency } = useMemo(
    () => transformBasketAplusToItems(fragmentData.clusters),
    [fragmentData.clusters],
  );

  const { controlled, hasSaveError } = useAplusSelection({
    items,
    toggleService: (serviceUuid) => toggleServiceProduct({ locale, lineItemId, serviceUuid }),
    setServiceCount: (serviceUuid, count) =>
      toggleServiceProduct({ locale, lineItemId, serviceUuid, count }),
  });

  return (
    <AplusAccordion
      items={items}
      currency={currency}
      quantity={quantity}
      controlled={controlled}
      hasSaveError={hasSaveError}
    />
  );
};
